import React, { useEffect, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Button, Input, Table } from "antd";
import ChartRenderer from "./ChartRenderer";

const MessageRenderer = ({ userId, tool, toolInput, thought, finalOutput }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleAccordion = () => setIsOpen(!isOpen);
  const [queryResult, setQueryResult] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sqlQuery, setSqlQuery] = useState("");
  const { TextArea } = Input;

  useEffect(() => {
    addQuery();
  }, [toolInput]);
  const addQuery = () => {
    if (toolInput) {
      const parsed = JSON.parse(toolInput);
      if (!parsed?.query) return;
      setSqlQuery(parsed?.query);
    }
  };
  const halndleQueryResult = async (ti, recall) => {
    // console.log("SQL Query Result: ", ti);
    const parsed = JSON.parse(ti);
    if (parsed?.query) {
      setSidebarOpen(true);
      setLoading(true);
      // console.log("SQL Query: ", parsed.query);
      const response = await fetch(
        process.env.REACT_APP_API_URL + "/run_sql_query",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: recall ? sqlQuery : parsed.query,
            user_id: userId,
          }),
        }
      );
      if (!response.ok) {
        console.error("Error fetching SQL query result:", response.statusText);
        setLoading(false);
        setError("Error fetching SQL query result");
        return;
      }
      const data = await response.json();
      // console.log("SQL Query Result: ", data);
      setQueryResult(data?.results || []);
      setLoading(false);
      setError(null);
    }
  };

  // Function to parse and render content with charts
  const parseContentWithCharts = (content) => {
    // Regex to find ```chart blocks
    const chartRegex = /```chart\s*([\s\S]*?)\s*```/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = chartRegex.exec(content)) !== null) {
      // Add text before the chart
      if (match.index > lastIndex) {
        const textBefore = content.slice(lastIndex, match.index);
        if (textBefore.trim()) {
          parts.push({
            type: "text",
            content: textBefore,
            key: `text-${parts.length}`,
          });
        }
      }

      // Add the chart
      const chartJson = match[1].trim();
      parts.push({
        type: "chart",
        content: chartJson,
        key: `chart-${parts.length}`,
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after the last chart
    if (lastIndex < content.length) {
      const textAfter = content.slice(lastIndex);
      if (textAfter.trim()) {
        parts.push({
          type: "text",
          content: textAfter,
          key: `text-${parts.length}`,
        });
      }
    }

    // If no charts found, return the original content as text
    if (parts.length === 0) {
      parts.push({
        type: "text",
        content: content,
        key: "text-0",
      });
    }

    return parts;
  };

  const renderQueryResult = () => {
    if (loading)
      return (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          Loading...
        </div>
      );
    if (error)
      return (
        <>
          <div className="flex w-full justify-start items-start mb-[16px]">
            <TextArea
              rows={2}
              className="w-80 h-3 overflow-auto resize-none max-h-[100px]"
              placeholder="Add your query"
              value={sqlQuery}
              onChange={(e) => setSqlQuery(e.target.value, "99123")}
            />
            <Button
              className="ml-5"
              onClick={() => halndleQueryResult(toolInput, true)}
            >
              Run Query
            </Button>
          </div>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            Error: {error}
          </div>
        </>
      );
    if (!queryResult || queryResult.length === 0)
      return (
        <>
          <div className="flex w-full justify-start items-start mb-[16px]">
            <TextArea
              rows={2}
              className="w-80 h-3 overflow-hidden"
              placeholder="Add your query"
              value={sqlQuery}
              onChange={(e) => setSqlQuery(e.target.value, "99123")}
            />
            <Button
              className="ml-5"
              onClick={() => halndleQueryResult(toolInput, true)}
            >
              Run Query
            </Button>
          </div>
          <div
            className="ml-2"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            No results found
          </div>
        </>
      );

    // Assuming queryResult is an array of objects
    const columns = Object.keys(queryResult[0]).map((key) => ({
      title: key.replace(/_/g, " ").toUpperCase(),
      dataIndex: key,
      key: key,
    }));
    columns.unshift({
      title: "SL",
      dataIndex: "sl",
      key: "sl",
    });

    // Filter data based on search text
    const filteredData = queryResult
      .filter((record) =>
        Object.values(record).some((value) =>
          String(value).toLowerCase().includes(searchText.toLowerCase())
        )
      )
      .map((record, idx) => {
        const newRecord = { ...record, sl: idx + 1 };
        return newRecord;
      });

    return (
      <>
        <div className="flex w-full justify-between items-start mb-[16px]">
          <div className="flex items-start">
            <TextArea
              rows={2}
              className="w-96 h-3 overflow-hidden"
              placeholder="Add your query"
              value={sqlQuery}
              onChange={(e) => setSqlQuery(e.target.value, "99123")}
            />
            <Button
              className="ml-5"
              onClick={() => halndleQueryResult(toolInput, true)}
            >
              Run Query
            </Button>
          </div>
          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                // marginBottom: "16px",
                borderRadius: "4px",
                height: "30px",
                padding: "8px",
                width: "200px",
                fontSize: "12px",
              }}
            />
          </div>
        </div>
        <div className="text-xs font-bold mb-1">
          Result Count: {filteredData.length}
        </div>
        <div className="w-full overflow-x-auto">
          <Table
            showHeader={true}
            dataSource={filteredData}
            style={{ width: "250%", maxWidth: "250%" }}
            columns={columns}
            scroll={{ y: 195 }}
            pagination={{ pageSize: 6 }}
          />
        </div>
      </>
    );
  };

  // debugger;
  if (tool && toolInput)
    return (
      <>
        <div className="custom-accordion">
          <div className="accordion-header" onClick={toggleAccordion}>
            <b>Agent Tool Call: {tool}</b>
            <span className="accordion-icon">
              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </span>
          </div>
          {isOpen && (
            <div className="accordion-content prose">
              <Markdown remarkPlugins={[remarkGfm]} className="log-content">
                {toolInput.replace(/\\n/g, "\n -> ").trim()}
              </Markdown>
            </div>
          )}
        </div>
        {tool === "sql_db_query" ? (
          <div
            onClick={() => {
              halndleQueryResult(toolInput);
              addQuery();
            }}
            className="view-results"
          >
            View Results
          </div>
        ) : null}
        {tool === "sql_db_query" && sidebarOpen ? (
          <div className="sidebar">
            <div className="sidebar-header">
              <div className="font-bold text-lg">SQL Query Result</div>
              <button onClick={() => setSidebarOpen(false)}>Close</button>
            </div>
            <div className="sidebar-content">{renderQueryResult()}</div>
            {/* {loading && <div>Loading...</div>} */}
          </div>
        ) : null}
      </>
    );
  else if (finalOutput) {
    // Parse content to separate text and charts
    const contentParts = parseContentWithCharts(finalOutput);

    setTimeout(() => {
      const pTags = document.querySelectorAll(".final-content p");

      [...pTags].map((p) => {
        if (p.innerText.includes("#Caution: low confidence results#")) {
          const words = p.innerText.split(" ");
          let newHTML = "";
          words.map((word) => {
            if (
              word === "#Caution:" ||
              word === "low" ||
              word === "confidence" ||
              word === "results#" ||
              word === "results#."
            ) {
              newHTML =
                newHTML +
                `<p style='color:#d303fc;font-weight:bold ;display:inline-block; margin-right:4px'>${word}</p>`;
            } else {
              newHTML = newHTML + `${word} `;
            }
          });
          p.innerHTML = newHTML;
          // //  p.style.fontWeight="bold"
          // //  p.style.color =""
          // console.log(words,'99123')
        }
      });
    }, 800);

    return (
      <div>
        <div className="final-content">
          {contentParts.map((part) => {
            if (part.type === "text") {
              return (
                <div key={part.key} className="prose">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {part.content}
                  </Markdown>
                </div>
              );
            } else if (part.type === "chart") {
              return <ChartRenderer key={part.key} chartData={part.content} />;
            }
            return null;
          })}
        </div>
      </div>
    );
  } else if (thought)
    return (
      <div className="prose">
        <Markdown>
          {"_**Thought**_: " + thought.replace(/\\n/g, "\n -> ").trim()}
        </Markdown>
      </div>
    );
  else return null;
};

export default MessageRenderer;
